import { useGetMe } from '@shared/services/get-me';
import Loader from '@shared/components/reusables/loader';
import { useGetMemberships } from '@src/shared/services/get-memberships';

import { type ReactNode } from 'react';
import { useGetSubscription } from '@src/shared/services/get-subscription';
import NotFound from '@src/shared/pages/not-found';

/**
 * DataLoader Component
 *
 * This component serves as a data loading wrapper that ensures all necessary user data
 * is loaded before rendering its children. It handles:
 * 1. User authentication data
 * 2. Organization membership data
 * 3. User role data
 * 4. Subscription data
 *
 * The component shows a loading state while any of these data points are being fetched.
 */
export const DataLoader = ({ children }: { children: ReactNode }) => {
  const { isSuccess: isMembershipsSuccess, isError: isMembershipsError } =
    useGetMemberships();

  const { isSuccess: isMemberSuccess, isError: isMemberError } = useGetMe({
    enabled: !!isMembershipsSuccess,
  });

  const { isSuccess: isSubscriptionSuccess } = useGetSubscription({
    enabled: !!isMemberSuccess && isMembershipsSuccess,
  });

  if (isMemberSuccess && isMembershipsSuccess && isSubscriptionSuccess) {
    return <>{children}</>;
  }

  if (isMembershipsError) {
    throw new Error('Failed to load memberships');
  }

  if (isMemberError) {
    return <NotFound />;
  }

  return <Loader />;
};
